import React, { useContext, useState, useEffect } from 'react';
import { isEqual } from 'lodash';
import { Helmet } from 'react-helmet';
import { history } from './history';
import { t } from '../locales/translate';
import { useDebounce } from '../util/async';

/* INTERFACES */
interface MetaContextContent {
  props: Props;
  set: (props: Props) => void;
}

interface Props {
  title?: string;
  breadcrumbs?: Array<{ title: string; url?: string }>;
  themeColor?: string;
}

/* VARIABLES */
const MetaContext = React.createContext<MetaContextContent | undefined>(undefined);

/* HOOKS */
export function useMeta(props: Props): void {
  const context = useContext(MetaContext);
  useEffect(() => {
    if (context) {
      context.set(props);
    } else {
      throw new Error('Cannot use `useMeta` outside of a `MetaProvider`');
    }
  }, [props]);
}

export function useMetaContent(): Props {
  const context = useContext(MetaContext);

  if (context) {
    return context.props;
  } else {
    throw new Error('Cannot use `useMetaContent` outside of a `MetaProvider`');
  }
}

/* METHODS */
export function MetaProvider({ children }: { children: JSX.Element }): JSX.Element {
  const [props, setProps] = useState<Props>({});
  const debounce = useDebounce(0.1);
  function set(p: Props): void {
    if (!isEqual(p, props)) {
      debounce(() => {
        setProps(p);
      });
    }
  }

  useEffect(() => {
    history.listen(() => {
      setProps({});
    });
  }, []);

  const { title } = props;
  return (
    <MetaContext.Provider value={{ props, set }}>
      <Helmet>
        <title>{title ? `${title} | ` : ''} Krasser Stoff</title>
        <meta name="theme-color" content={props.themeColor ?? '#000'} />
      </Helmet>
      {children}
    </MetaContext.Provider>
  );
}

export function getPageTitle<T>(loading: boolean, name?: string, item?: T): string {
  if (loading) {
    return t.actions.loading('');
  } else if (!item) {
    return t.errors.notFound;
  } else {
    return name || '';
  }
}
